<template>
  <div class="page-wrapper admin-stats-sheet" :class="isMember ? 'admin-stats-sheet--cols-3' : 'admin-stats-sheet--cols-4'">
    <div class="stats-row">
      <div class="mini-stat" v-for="s in displayStats" :key="s.label">
        <div class="mini-stat-icon" :style="{ background: s.bg, color: s.color }"><component :is="s.icon" /></div>
        <div><p class="mini-stat-val">{{ s.value }}</p><p class="mini-stat-label">{{ s.label }}</p></div>
      </div>
    </div>

    <div class="page-toolbar">
      <div class="page-toolbar-left checkin-toolbar-left">
        <div class="checkin-date-wrap">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始"
            end-placeholder="结束"
            size="default"
            value-format="YYYY-MM-DD"
            style="width: 100%"
            class="checkin-range-picker"
            @change="onDateRangeChange"
          />
        </div>
      </div>
      <div class="page-toolbar-right">
        <button v-if="isMember" class="btn-primary" :disabled="checkinSubmitting" @click="memberCheckin">
          <el-icon><Plus /></el-icon><span>{{ checkinSubmitting ? '打卡中...' : '立即打卡' }}</span>
        </button>
        <button v-if="isAdmin" class="btn-primary" @click="openManualDialog">
          <el-icon><Plus /></el-icon><span>手动打卡</span>
        </button>
      </div>
    </div>

    <div class="content-grid" :class="{ 'content-grid--full': !showSidePanel }">
      <div class="table-card">
        <div class="card-header-row">
          <h3>{{ isMember ? '我的打卡记录' : '打卡记录列表' }}</h3>
        </div>
        <el-table v-loading="loading" :data="records" style="width:100%" :header-cell-style="headerStyle">
          <el-table-column type="index" label="#" width="55" :index="indexMethod" />
          <el-table-column v-if="!isMember" label="会员信息" min-width="160">
            <template #default="{ row }">
              <div class="member-cell">
                <div class="avatar" :style="{ background: row.avatarBg }">{{ (row.memberName || '?').charAt(0) }}</div>
                <div>
                  <p class="member-name">{{ row.memberName }}</p>
                  <p class="member-phone">{{ row.memberType || '—' }}</p>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="checkinDate" label="打卡日期" width="130" />
          <el-table-column prop="checkinTime" label="打卡时间" width="120" />
          <el-table-column prop="location" label="打卡地点" min-width="120" />
          <el-table-column prop="consecutiveDays" label="连续天数" width="100" />
          <el-table-column label="操作" width="80" fixed="right">
            <template #default="{ row }">
              <button class="act-btn del" title="删除" @click="deleteRecord(row)"><el-icon><Delete /></el-icon></button>
            </template>
          </el-table-column>
        </el-table>
        <div class="table-footer">
          <span class="total-count">共 {{ total }} 条记录</span>
          <el-pagination
            v-model:current-page="currentPage"
            :page-size="pageSize"
            :total="total"
            layout="prev, pager, next"
            background
            small
            @current-change="loadRecords"
          />
        </div>
      </div>

      <div v-if="showSidePanel" class="side-panel">
        <div v-if="showHeatmap" class="heatmap-card">
          <div class="card-title">本月打卡热力</div>
          <div class="heatmap-grid">
            <div
              v-for="d in heatmapData"
              :key="d.day"
              class="heat-cell"
              :style="{ background: d.checked ? 'rgba(0,98,255,0.75)' : 'var(--border-light)' }"
              :title="d.day + '日' + (d.checked ? '：已打卡' : '')"
            ></div>
          </div>
        </div>
        <div v-if="isAdmin" class="rank-card">
          <div class="card-title">本月打卡排行</div>
          <div v-if="rankList.length === 0" class="rank-empty">暂无数据</div>
          <div v-else class="rank-list">
            <div class="rank-item" v-for="(r, i) in rankList" :key="r.memberId">
              <div class="rank-num" :class="i < 3 ? 'top' : ''">{{ i + 1 }}</div>
              <div class="avatar sm" :style="{ background: r.color }">{{ (r.name || '?').charAt(0) }}</div>
              <div class="rank-info">
                <p class="rank-name">{{ r.name }}</p>
                <p class="rank-sub">本月 {{ r.count }} 次</p>
              </div>
              <div class="rank-bar-wrap">
                <div class="rank-bar" :style="{ width: rankMax ? (r.count / rankMax * 100) + '%' : '0%' }"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <el-dialog v-model="manualDialogVisible" title="手动打卡" width="420px" destroy-on-close @closed="manualForm = { memberId: null, location: '前台' }">
      <el-form label-width="80px">
        <el-form-item label="会员" required>
          <el-select v-model="manualForm.memberId" filterable placeholder="选择会员" style="width:100%">
            <el-option v-for="m in members" :key="m.memberId" :label="m.name || m.username" :value="m.memberId" />
          </el-select>
        </el-form-item>
        <el-form-item label="地点">
          <el-input v-model="manualForm.location" placeholder="如：前台" maxlength="100" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="manualDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="checkinSubmitting" @click="submitManualCheckin">确认打卡</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { Plus, Delete, Stopwatch, TrendCharts, UserFilled, Calendar } from '@element-plus/icons-vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import api from '@/api/request';

const colors = ['#0062FF', '#10B981', '#EF4444', '#F59E0B', '#8B5CF6', '#06B6D4', '#EC4899'];

export default {
  name: 'CheckinView',
  components: { Plus, Delete, Stopwatch, TrendCharts, UserFilled, Calendar },
  data() {
    const now = new Date();
    return {
      loading: false,
      checkinSubmitting: false,
      dateRange: null,
      currentPage: 1,
      pageSize: 10,
      total: 0,
      records: [],
      members: [],
      manualDialogVisible: false,
      manualForm: { memberId: null, location: '前台' },
      monthStats: { totalDays: 0, consecutiveDays: 0, checkedDates: [] },
      rankList: [],
      statsYear: now.getFullYear(),
      statsMonth: now.getMonth() + 1,
      todayCount: 0,
      monthTotalCheckins: 0,
    };
  },
  computed: {
    userRole() {
      return this.$store.getters.userRole;
    },
    typeId() {
      return this.$store.getters.typeId;
    },
    isAdmin() {
      return this.userRole === 'admin';
    },
    isCoach() {
      return this.userRole === 'coach';
    },
    isMember() {
      return this.userRole === 'member';
    },
    showSidePanel() {
      return this.isMember || this.isAdmin;
    },
    showHeatmap() {
      return this.isMember;
    },
    displayStats() {
      if (this.isMember) {
        return [
          { label: '本月打卡', value: String(this.monthStats.totalDays || 0), icon: 'Calendar', bg: 'rgba(0,98,255,0.1)', color: '#0062FF' },
          { label: '连续打卡', value: `${this.monthStats.consecutiveDays || 0}天`, icon: 'TrendCharts', bg: 'rgba(16,185,129,0.1)', color: '#10B981' },
          { label: '今日状态', value: this.todayChecked ? '已打卡' : '未打卡', icon: 'Stopwatch', bg: 'rgba(245,158,11,0.1)', color: '#F59E0B' },
        ];
      }
      return [
        { label: '今日打卡', value: String(this.todayCount), icon: 'Stopwatch', bg: 'rgba(0,98,255,0.1)', color: '#0062FF' },
        { label: '本月累计', value: String(this.monthTotalCheckins), icon: 'TrendCharts', bg: 'rgba(16,185,129,0.1)', color: '#10B981' },
        { label: '活跃会员', value: String(this.rankList.length), icon: 'UserFilled', bg: 'rgba(245,158,11,0.1)', color: '#F59E0B' },
        { label: '连续最高', value: this.rankList.length ? `${this.rankList[0].maxConsecutive || 0}天` : '0天', icon: 'Calendar', bg: 'rgba(139,92,246,0.1)', color: '#8B5CF6' },
      ];
    },
    todayChecked() {
      const today = this.formatDate(new Date());
      return (this.monthStats.checkedDates || []).includes(today);
    },
    heatmapData() {
      const daysInMonth = new Date(this.statsYear, this.statsMonth, 0).getDate();
      const set = new Set(this.monthStats.checkedDates || []);
      return Array.from({ length: daysInMonth }, (_, i) => {
        const day = i + 1;
        const d = `${this.statsYear}-${String(this.statsMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        return { day, checked: set.has(d) };
      });
    },
    rankMax() {
      if (!this.rankList.length) return 0;
      return Math.max(...this.rankList.map((r) => r.count), 1);
    },
    headerStyle() {
      return { background: 'var(--bg-gray)', color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '600' };
    },
  },
  mounted() {
    this.loadRecords();
    if (this.isMember) {
      this.loadMonthStatsForMember();
    }
    if (this.isAdmin) {
      this.loadMembers();
      this.loadAdminExtras();
    }
  },
  methods: {
    formatDate(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    indexMethod(index) {
      return (this.currentPage - 1) * this.pageSize + index + 1;
    },
    onDateRangeChange() {
      this.currentPage = 1;
      this.loadRecords();
    },
    buildPageParams() {
      const params = { pageNumber: this.currentPage, pageSize: this.pageSize };
      if (this.dateRange && this.dateRange.length === 2) {
        params.startDate = this.dateRange[0];
        params.endDate = this.dateRange[1];
      }
      return params;
    },
    async loadRecords() {
      this.loading = true;
      try {
        const res = await api.getCheckInPage(this.buildPageParams());
        if (res.data?.code === 200) {
          const page = res.data.data || {};
          const list = page.records || [];
          this.total = page.totalRow || 0;
          this.records = list.map((r) => {
            const t = r.checkinTime ? String(r.checkinTime) : '';
            return {
              ...r,
              checkinDate: r.checkinDate || (t ? t.substring(0, 10) : '-'),
              checkinTime: t ? t.substring(11, 19) : '-',
              avatarBg: colors[(r.memberId || 0) % colors.length],
            };
          });
        }
      } catch {
        ElMessage.error('加载打卡记录失败');
      } finally {
        this.loading = false;
      }
    },
    async loadAdminExtras() {
      try {
        const today = this.formatDate(new Date());
        const res = await api.getCheckInPage({ pageNumber: 1, pageSize: 500, startDate: today, endDate: today });
        if (res.data?.code === 200) {
          this.todayCount = res.data.data?.totalRow || 0;
        }
        const monthStart = `${this.statsYear}-${String(this.statsMonth).padStart(2, '0')}-01`;
        const monthEnd = `${this.statsYear}-${String(this.statsMonth).padStart(2, '0')}-${new Date(this.statsYear, this.statsMonth, 0).getDate()}`;
        const allRes = await api.getCheckInPage({ pageNumber: 1, pageSize: 500, startDate: monthStart, endDate: monthEnd });
        if (allRes.data?.code === 200) {
          const list = allRes.data.data?.records || [];
          const map = {};
          list.forEach((r) => {
            const id = r.memberId;
            if (!map[id]) {
              map[id] = { memberId: id, name: r.memberName, count: 0, maxConsecutive: 0, color: colors[id % colors.length] };
            }
            map[id].count += 1;
            const cd = r.consecutiveDays || 0;
            if (cd > map[id].maxConsecutive) map[id].maxConsecutive = cd;
          });
          this.rankList = Object.values(map).sort((a, b) => b.count - a.count).slice(0, 5);
          this.monthTotalCheckins = allRes.data.data?.totalRow || list.length;
        }
      } catch { /* ignore */ }
    },
    async loadMembers() {
      try {
        const res = await api.getMemberList();
        if (res.data?.code === 200) {
          this.members = (res.data.data || []).map((m) => ({
            ...m,
            name: m.name || m.username,
          }));
        }
      } catch { /* ignore */ }
    },
    openManualDialog() {
      this.manualForm = { memberId: null, location: '前台' };
      this.manualDialogVisible = true;
    },
    async memberCheckin() {
      if (!this.typeId) {
        ElMessage.warning('会员信息异常，请重新登录');
        return;
      }
      if (this.todayChecked) {
        ElMessage.warning('今日已打卡');
        return;
      }
      this.checkinSubmitting = true;
      try {
        const res = await api.saveCheckIn({ memberId: this.typeId, location: '健身房' });
        if (res.data?.code === 200) {
          ElMessage.success('打卡成功');
          this.currentPage = 1;
          await this.loadRecords();
          await this.loadMonthStatsForMember();
        } else {
          ElMessage.error(res.data?.message || '打卡失败');
        }
      } catch {
        ElMessage.error('打卡失败');
      } finally {
        this.checkinSubmitting = false;
      }
    },
    async loadMonthStatsForMember() {
      if (!this.typeId) return;
      try {
        const res = await api.getCheckInStats(this.typeId, this.statsYear, this.statsMonth);
        if (res.data?.code === 200) {
          this.monthStats = res.data.data || {};
        }
      } catch { /* ignore */ }
    },
    async submitManualCheckin() {
      if (!this.manualForm.memberId) {
        ElMessage.warning('请选择会员');
        return;
      }
      this.checkinSubmitting = true;
      try {
        const res = await api.saveCheckIn({
          memberId: this.manualForm.memberId,
          location: this.manualForm.location || '前台',
        });
        if (res.data?.code === 200) {
          ElMessage.success('手动打卡成功');
          this.manualDialogVisible = false;
          this.loadRecords();
          this.loadAdminExtras();
        } else {
          ElMessage.error(res.data?.message || '打卡失败');
        }
      } catch {
        ElMessage.error('打卡失败');
      } finally {
        this.checkinSubmitting = false;
      }
    },
    async deleteRecord(row) {
      try {
        await ElMessageBox.confirm('确定删除该打卡记录？删除后不可恢复。', '提示', { type: 'warning' });
        const res = await api.removeCheckIn(row.checkinId);
        if (res.data?.code === 200) {
          ElMessage.success('删除成功');
          if (this.records.length === 1 && this.currentPage > 1) {
            this.currentPage -= 1;
          }
          this.loadRecords();
          if (this.isMember) this.loadMonthStatsForMember();
          else if (this.isAdmin) this.loadAdminExtras();
        } else {
          ElMessage.error(res.data?.message || '删除失败');
        }
      } catch (e) {
        if (e !== 'cancel') ElMessage.error('删除失败');
      }
    },
  },
};
</script>

<style scoped>
.page-wrapper { padding: 0; background: var(--bg-base); min-height: 100vh; }
.checkin-toolbar-left { min-width: 0; }
.checkin-date-wrap {
  flex: 1;
  min-width: 0;
  max-width: min(440px, 100%);
}
.checkin-toolbar-left :deep(.checkin-range-picker) {
  max-width: 100%;
}
.btn-primary { display: inline-flex; align-items: center; gap: 6px; padding: 0 18px; height: 38px; background: var(--gradient-primary); color: white; border: none; border-radius: var(--radius-md); font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.3s; }
.btn-primary:hover:not(:disabled) { transform: translateY(-1px); box-shadow: var(--shadow-glow); }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.mini-stat { background: var(--bg-white); border: 1px solid var(--border-color); border-radius: var(--radius-lg); padding: 16px 20px; display: flex; align-items: center; gap: 14px; transition: all 0.3s; }
.mini-stat-icon { width: 44px; height: 44px; border-radius: var(--radius-md); display: flex; align-items: center; justify-content: center; font-size: 20px; flex-shrink: 0; }
.mini-stat-val { font-size: 22px; font-weight: 700; color: var(--text-primary); margin: 0 0 2px; font-family: 'Inter', sans-serif; }
.mini-stat-label { font-size: 12px; color: var(--text-muted); margin: 0; }
.content-grid { display: grid; grid-template-columns: 1fr 320px; gap: 20px; }
.content-grid--full { grid-template-columns: 1fr; }
.table-card { background: var(--bg-white); border: 1px solid var(--border-color); border-radius: var(--radius-lg); overflow: hidden; }
.card-header-row { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; border-bottom: 1px solid var(--border-color); }
.card-header-row h3 { font-size: 15px; font-weight: 600; color: var(--text-primary); margin: 0; }
.member-cell { display: flex; align-items: center; gap: 10px; }
.avatar { width: 34px; height: 34px; border-radius: var(--radius-md); display: flex; align-items: center; justify-content: center; color: white; font-weight: 700; font-size: 14px; flex-shrink: 0; }
.avatar.sm { width: 28px; height: 28px; font-size: 12px; }
.member-name { font-size: 14px; font-weight: 600; color: var(--text-primary); margin: 0 0 2px; }
.member-phone { font-size: 12px; color: var(--text-muted); margin: 0; }
.act-btn { width: 30px; height: 30px; border: none; border-radius: var(--radius-sm); display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.3s; font-size: 14px; }
.act-btn.del { background: rgba(239,68,68,0.1); color: #EF4444; }
.act-btn.del:hover { background: #EF4444; color: white; }
.table-footer { display: flex; align-items: center; justify-content: space-between; padding: 14px 20px; border-top: 1px solid var(--border-color); flex-wrap: wrap; gap: 10px; }
.total-count { font-size: 13px; color: var(--text-muted); }
.side-panel { display: flex; flex-direction: column; gap: 16px; }
.heatmap-card, .rank-card { background: var(--bg-white); border: 1px solid var(--border-color); border-radius: var(--radius-lg); padding: 16px; }
.card-title { font-size: 14px; font-weight: 600; color: var(--text-primary); margin-bottom: 14px; }
.heatmap-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px; margin-bottom: 8px; }
.heat-cell { aspect-ratio: 1; border-radius: 3px; cursor: default; }
.rank-empty { font-size: 13px; color: var(--text-muted); padding: 8px 0; }
.rank-list { display: flex; flex-direction: column; gap: 10px; }
.rank-item { display: flex; align-items: center; gap: 8px; }
.rank-num { width: 22px; height: 22px; border-radius: 50%; background: var(--bg-gray); color: var(--text-muted); font-size: 12px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.rank-num.top { background: var(--primary-blue); color: white; }
.rank-info { flex: 1; min-width: 0; }
.rank-name { font-size: 13px; font-weight: 600; color: var(--text-primary); margin: 0 0 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rank-sub { font-size: 11px; color: var(--text-muted); margin: 0; }
.rank-bar-wrap { width: 60px; height: 4px; background: var(--border-light); border-radius: 2px; overflow: hidden; flex-shrink: 0; }
.rank-bar { height: 100%; background: var(--gradient-primary); border-radius: 2px; }

@media (max-width: 900px) {
  .content-grid { grid-template-columns: 1fr; }
}
@media (max-width: 768px) {
  .page-toolbar { flex-direction: column; align-items: stretch; gap: 10px; }
  .checkin-date-wrap { max-width: none; }
  .page-toolbar-right .btn-primary { width: 100%; justify-content: center; }
}
</style>
